import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, tap } from 'rxjs';
import { Global } from '@services/core/Global';
import { MicrosoftAuthService } from '@services/core/authentication.service';
import { EntitiesService } from '@services/core/entities.service';

@Injectable({
  providedIn: 'root',
})
export class CatalogService {
  // Signals para almacenar los catálogos
  public brands = signal<any[]>([]);
  public models = signal<any[]>([]);
  public partyTypes = signal<any[]>([]);
  public statuses = signal<any[]>([]);
  public roles = signal<any[]>([]);
  public menus = signal<any[]>([]);
  public groups = signal<any[]>([]);
  public entities = signal<any[]>([]);
  public processes = signal<any[]>([]);
  public processCategories = signal<any[]>([]);
  public documentCategories = signal<any[]>([]);
  public documentOwners = signal<any[]>([]);
  public documentTypes = signal<any[]>([]);
  public documentRequirements = signal<any[]>([]);
  public rules = signal<any[]>([]);

  // Estado de carga
  public loading = signal<boolean>(false);
  public loaded = signal<boolean>(false);

  private url: string;
  private http = inject(HttpClient);
  private microsoftAuth = inject(MicrosoftAuthService);
  private entitiesService = inject(EntitiesService);

  constructor() {
    this.url = Global.url;
  }

  validadted() {
    return this.loaded() || this.loading();
  }

  // Carga inicial de todos los catálogos principales
  public loadCatalogs() {
    if (this.validadted()) {
      return;
    }

    this.loading.set(true);
    const brands$ = this.loadBrands();
    const models$ = this.loadModels();
    const partyTypes$ = this.loadPartyTypes();
    const statuses$ = this.loadStatuses();
    const roles$ = this.loadRoles();
    const menus$ = this.loadMenus();
    const groups$ = this.loadGroups();
    const entities$ = this.entitiesService.get();
    const processes$ = this.loadProcesses();
    const processCategories$ = this.loadProcessCategories();
    const docCategories$ = this.loadDocumentCategories();
    const docOwners$ = this.loadDocumentOwners();
    const docTypes$ = this.loadDocumentTypes();
    const docRequirements$ = this.loadDocumentRequirements();
    const rules$ = this.loadRules();

    forkJoin({
      brands: brands$,
      models: models$,
      partyTypes: partyTypes$,
      statuses: statuses$,
      roles: roles$,
      menus: menus$,
      groups: groups$,
      entities: entities$,
      processes: processes$,
      processCategories: processCategories$,
      docCategories: docCategories$,
      docOwners: docOwners$,
      docTypes: docTypes$,
      docRequirements: docRequirements$,
      rules: rules$
    })
      .pipe(
        tap(
          ({
            brands,
            models,
            partyTypes,
            statuses,
            roles,
            menus,
            groups,
            entities,
            processes,
            processCategories,
            docCategories,
            docOwners,
            docTypes,
            docRequirements,
            rules
          }) => {
            this.brands.set(brands);
            this.models.set(models);
            this.partyTypes.set(partyTypes);
            this.statuses.set(statuses);
            this.roles.set(roles);
            this.menus.set(menus);
            this.groups.set(groups);
            this.entities.set(this.sortEntities(entities));
            this.processes.set(processes);
            this.processCategories.set(processCategories ?? []);
            this.documentCategories.set(docCategories ?? []);
            this.documentOwners.set(docOwners ?? []);
            this.documentTypes.set(docTypes ?? []);
            this.documentRequirements.set(docRequirements ?? []);
            this.rules.set(rules ?? []);
            this.loaded.set(true);
            this.loading.set(false);
          },
        ),
      )
      .subscribe({
        error: (err) => {
          console.error('Error cargando catálogos', err);
          this.loading.set(false);
        },
      });
  }

  loadBrands() {
    return this.http.get<any[]>(this.url + 'V1/brand-ref');
  }

  reloadBrands(): void {
    this.loadBrands().subscribe({
      next: (list) => this.brands.set(list ?? []),
      error: (err) => console.error('Error recargando brands', err)
    });
  }

  upsertBrand(brand: any): void {
    this.brands.set(this.mergeByCode(this.brands(), brand));
  }

  removeBrand(code: any): void {
    this.brands.set(this.brands().filter((b) => Number(b.code) !== Number(code)));
  }

  loadModels() {
    return this.http.get<any[]>(this.url + 'V1/vehicle-model-ref');
  }

  reloadModels(): void {
    this.loadModels().subscribe({
      next: (list) => this.models.set(list ?? []),
      error: (err) => console.error('Error recargando models', err)
    });
  }

  upsertModel(model: any): void {
    this.models.set(this.mergeByCode(this.models(), model));
  }

  removeModel(code: any): void {
    this.models.set(this.models().filter((m) => Number(m.code) !== Number(code)));
  }

  removeModelsByBrand(brandCode: any): void {
    this.models.set(this.models().filter((m) => Number(m.brandCode) !== Number(brandCode)));
  }

  loadPartyTypes() {
    return this.http.get<any[]>(this.url + 'V1/party-type-ref');
  }
  loadStatuses() {
    return this.http.get<any[]>(this.url + 'V1/status-ref');
  }

  /** Recarga solo el catálogo de estados (p. ej. tras el mantenedor). */
  reloadStatuses(): void {
    this.loadStatuses().subscribe({
      next: (list) => this.statuses.set(list ?? []),
      error: (err) => console.error('Error recargando statuses', err)
    });
  }

  reloadEntities(): void {
    this.entitiesService.get().subscribe({
      next: (list) => this.entities.set(this.sortEntities(list)),
      error: (err) => console.error('Error recargando entidades', err)
    });
  }

  private mergeByCode(list: any[], item: any): any[] {
    if (!item) return list ?? [];
    const code = Number(item.code);
    const current = [...(list ?? [])];
    const idx = current.findIndex((x) => Number(x.code) === code);
    if (idx < 0) return [...current, item];
    current[idx] = { ...current[idx], ...item };
    return current;
  }

  private sortEntities(list: any[] | null | undefined) {
    return [...(list ?? [])].sort((a, b) => {
      const aActive = a.isActive === false ? 0 : 1;
      const bActive = b.isActive === false ? 0 : 1;
      if (aActive !== bActive) return bActive - aActive;
      return Number(a.code) - Number(b.code);
    });
  }

  loadRoles() {
    return this.http.get<any[]>(this.url + 'V1/roles');
  }

  /** Recarga solo el catálogo de roles (p. ej. tras el mantenedor). */
  reloadRoles(): void {
    this.loadRoles().subscribe({
      next: (list) => this.roles.set(list ?? []),
      error: (err) => console.error('Error recargando roles', err)
    });
  }

  loadMenus() {
    return this.http.get<any[]>(this.url + 'V1/menus');
  }

  /** Recarga el catálogo de menús (incluye id/parentId). */
  reloadMenus(): void {
    this.loadMenus().subscribe({
      next: (list) => this.menus.set(Array.isArray(list) ? list : []),
      error: (err) => console.error('Error recargando menus', err)
    });
  }
  loadGroups() {
    return this.http.get<any[]>(this.url + 'V1/group-ref');
  }
  loadProcesses() {
    return this.http.get<any[]>(this.url + 'V1/process-documents');
  }

  loadProcessCategories() {
    return this.http.get<any[]>(this.url + 'V1/process-category-ref', { params: { activeOnly: 'true' } });
  }

  /** Recarga categorías de proceso (p. ej. tras el mantenedor). */
  reloadProcessCategories(): void {
    this.loadProcessCategories().subscribe({
      next: (list) => this.processCategories.set(list ?? []),
      error: (err) => console.error('Error recargando process categories', err)
    });
  }

  loadDocumentCategories() {
    return this.http.get<any[]>(this.url + 'V1/document-categories');
  }

  loadDocumentOwners() {
    return this.http.get<any[]>(this.url + 'V1/document-owners');
  }

  loadDocumentTypes() {
    return this.http.get<any[]>(this.url + 'V1/document-types');
  }

  loadDocumentRequirements() {
    return this.http.get<any[]>(this.url + 'V1/process-document-ref');
  }

  loadRules() {
    return this.http.get<any[]>(this.url + 'V1/rule');
  }

  /** Recarga reglas (mantenedor o socket catalogChanged). */
  reloadRules(): void {
    this.loadRules().subscribe({
      next: (list) => this.rules.set(Array.isArray(list) ? list : []),
      error: (err) => console.error('Error recargando rules', err)
    });
  }

  /** Recarga procesos del catálogo. */
  reloadProcesses(): void {
    this.loadProcesses().subscribe({
      next: (list) => this.processes.set(list ?? []),
      error: (err) => console.error('Error recargando processes', err)
    });
  }

  /** Recarga requisitos de documentos por proceso. */
  reloadDocumentRequirements(): void {
    this.loadDocumentRequirements().subscribe({
      next: (list) => this.documentRequirements.set(Array.isArray(list) ? list : []),
      error: (err) => console.error('Error recargando document requirements', err)
    });
  }

  /** Recarga tipos de documento. */
  reloadDocumentTypes(): void {
    this.loadDocumentTypes().subscribe({
      next: (list) => this.documentTypes.set(Array.isArray(list) ? list : []),
      error: (err) => console.error('Error recargando document types', err)
    });
  }

  /** Upsert local de un tipo (tras crear/activar). */
  upsertDocumentType(doc: any): void {
    if (!doc?.slug) return;
    const list = [...(this.documentTypes() ?? [])];
    const idx = list.findIndex((t) => t.slug === doc.slug);
    if (idx >= 0) list[idx] = { ...list[idx], ...doc };
    else list.push(doc);
    this.documentTypes.set(list);
  }

  /** Aplica scopes de catalogChanged (socket). */
  applyCatalogChanged(scopes?: string[] | null): void {
    const list = Array.isArray(scopes) && scopes.length ? scopes.map((s) => String(s).toLowerCase()) : ['rules'];
    if (list.includes('rules') || list.includes('all')) this.reloadRules();
    if (list.includes('processes') || list.includes('all')) this.reloadProcesses();
    if (list.includes('processcategories') || list.includes('process-categories') || list.includes('all')) {
      this.reloadProcessCategories();
    }
    if (list.includes('entities') || list.includes('all')) this.reloadEntities();
    if (
      list.includes('documentrequirements') ||
      list.includes('document-requirements') ||
      list.includes('processdocumentref') ||
      list.includes('all')
    ) {
      this.reloadDocumentRequirements();
    }
    if (
      list.includes('documenttypes') ||
      list.includes('document-types') ||
      list.includes('all')
    ) {
      this.reloadDocumentTypes();
    }
  }

  getRuleBySlug(slug: string | null | undefined): any | undefined {
    if (!slug) return undefined;
    const key = String(slug).toUpperCase();
    return this.rules().find((r: any) => String(r.slug || '').toUpperCase() === key);
  }

  // Helpers de resolución de entidades
  getEntityByCode(code: number | string | null | undefined): any | undefined {
    if (code == null || code === '') return undefined;
    const num = Number(code);
    return this.entities().find((e: any) => Number(e.code) === num);
  }

  getEntityName(code: number | string | null | undefined, fallback = ''): string {
    const entity = this.getEntityByCode(code);
    return entity?.name || fallback;
  }

  // Helpers de resolución
  getStatusById(id: number) {
    return this.statuses().find(s => s.id === id);
  }

  getStatusBySlug(slug: string) {
    return this.statuses().find(s => s.slug === slug);
  }

  getStatusName(slug: string | null | undefined, fallback = '—'): string {
    if (!slug) return fallback;
    return this.getStatusBySlug(String(slug))?.name || String(slug);
  }

  getStatusIcon(slug: string | null | undefined, fallback = 'fas fa-circle'): string {
    if (!slug) return fallback;
    const icon = String(this.getStatusBySlug(String(slug))?.icon || '').trim();
    return icon || fallback;
  }

  getRoleByName(nameOrId: string | number | null | undefined): any | undefined {
    if (nameOrId == null || nameOrId === '') return undefined;
    const key = String(nameOrId).toLowerCase();
    return this.roles().find((r: any) =>
      String(r.slug || '').toLowerCase() === key
      || String(r.name || '').toLowerCase() === key
      || String(r.displayName || '').toLowerCase() === key
      || String(r.id) === String(nameOrId)
    );
  }

  getRoleDisplayName(nameOrId: string | number | null | undefined, fallback = ''): string {
    const role = this.getRoleByName(nameOrId);
    return role?.displayName || role?.slug || role?.name || (nameOrId != null && nameOrId !== '' ? String(nameOrId) : fallback);
  }

  getStatusByCode(code: number, category: string) {
    return this.statuses().find(s => s.code === code && s.category === category);
  }

  getProcessCategoryBySlug(slug: string | null | undefined) {
    if (!slug) return undefined;
    return this.processCategories().find((c: any) => c.slug === slug);
  }

  getProcessCategoryName(slug: string | null | undefined, fallback = '—'): string {
    if (!slug) return fallback;
    return this.getProcessCategoryBySlug(String(slug))?.name || String(slug);
  }

  getCategoryName(slug: string) {
    return this.documentCategories().find(c => c.slug === slug)?.name || slug;
  }

  getOwnerName(slug: string) {
    return this.documentOwners().find(o => o.slug === slug)?.name || slug;
  }

  getDocumentTypeBySlug(slug: string) {
    return this.documentTypes().find(d => d.slug === slug);
  }

  getDocumentTypeName(slug: string | null | undefined, fallback = '—'): string {
    if (!slug) return fallback;
    return this.getDocumentTypeBySlug(String(slug))?.name ?? String(slug);
  }

  /** Requisitos configurados para un proceso (desde catálogo). */
  getRequirementsByProcess(processSlug: string | null | undefined): any[] {
    if (!processSlug) return [];
    const slug = String(processSlug);
    return (this.documentRequirements() ?? [])
      .filter((r: any) => String(r.processSlug) === slug)
      .map((r: any) => ({
        ...r,
        type: r.type ?? this.getDocumentTypeBySlug(r.typeSlug) ?? null
      }))
      .sort((a: any, b: any) => {
        const byOwner = String(a.ownerSlug || '').localeCompare(String(b.ownerSlug || ''), 'es');
        if (byOwner) return byOwner;
        const an = a.type?.name || this.getDocumentTypeName(a.typeSlug) || a.typeSlug || '';
        const bn = b.type?.name || this.getDocumentTypeName(b.typeSlug) || b.typeSlug || '';
        return String(an).localeCompare(String(bn), 'es');
      });
  }

  /** Reemplaza en memoria los requisitos de un proceso (tras guardar en mantenedor). */
  setRequirementsForProcess(processSlug: string, requirements: any[]): void {
    const slug = String(processSlug);
    const others = (this.documentRequirements() ?? []).filter((r: any) => String(r.processSlug) !== slug);
    const next = (requirements ?? []).map((r: any) => ({
      ...r,
      processSlug: slug,
      type: r.type ?? this.getDocumentTypeBySlug(r.typeSlug) ?? null
    }));
    this.documentRequirements.set([...others, ...next]);
  }
}
